/*
 * Copyright (c) 2024 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import React, { useEffect } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Cookies from "js-cookie";

import "react-toastify/dist/ReactToastify.min.css";
import "components/SnackbarNotification/toast-style.css";

import ApplicationError from "pages/NotFound";
import DriverAppLoginPage from "pages/DriverAppLogin";
import Notification from "application/components/Notification/Notification";
import { useCommonState } from "common/hooks";
import { useLazyGetConfigurationQuery } from "store/api/configurationApiSlice";
import ModuleLoading from "components/ModuleLoading/ModuleLoading";
import RequireAuthentication from "application/components/RequireAuthentication";
import { FE_CONFIGS_API_URL } from "store/api/constants";
import { LoginPage } from "pages/LoginWeb";
import { ID_TOKEN } from "pages/LoginWeb/constants";
import { useShiftClaimsPolling } from "common/hooks/useShiftClaimsPolling";
import { identifyAnalytics } from "common/analytics";

const SchedulePage = React.lazy(() => import(/* webpackChunkName: "schedule" */ "pages/Schedule"));
const AbsencePage = React.lazy(() => import(/* webpackChunkName: "absences" */ "pages/Absence"));
const ContactPage = React.lazy(() => import(/* webpackChunkName: "contacts" */ "pages/Contact"));

interface LocationState {
    from: {
        pathname: string;
    };
}

const AUTO_CLOSE_TOAST_DURATION = 5000;
const LIMIT_TOAST_COUNT = 2;

export default function Application() {
    const navigate = useNavigate();
    const location = useLocation();
    const { configuration } = useCommonState();

    useShiftClaimsPolling();

    const [getConfiguration, { isLoading, isSuccess: isConfigurationLoaded }] =
        useLazyGetConfigurationQuery();

    const { authToken: appToken, baseApiUrl, isDriverApp } = configuration;
    const webToken = Cookies.get(ID_TOKEN);
    useEffect(() => {
        const fetchConfiguration = async () => {
            await getConfiguration({ url: FE_CONFIGS_API_URL });
        };
        if ((appToken || webToken) && baseApiUrl && !isConfigurationLoaded) {
            fetchConfiguration();
        }
    }, [
        appToken,
        baseApiUrl,
        getConfiguration,
        isConfigurationLoaded,
        isDriverApp,
        navigate,
        webToken,
    ]);

    useEffect(() => {
        if (configuration?.driverFullName) {
            const { cityId, driverId, tenantId } = configuration;

            if (cityId && driverId && tenantId) {
                identifyAnalytics({ cityId, driverId, tenantId });
            }
        }
    }, [configuration]);

    useEffect(() => {
        if (isConfigurationLoaded) {
            const from = (location.state as LocationState)?.from?.pathname || "/schedule";
            navigate(from);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isConfigurationLoaded]);

    if (isLoading) {
        return <ModuleLoading isFullScreen={true} />;
    }

    const isAuthenticated = isConfigurationLoaded && !!(appToken || webToken);

    return (
        <>
            <Routes>
                <Route path="/driver-app-login" element={<DriverAppLoginPage />} />
                <Route path="/" element={<LoginPage />} />
                <Route element={<RequireAuthentication isAuthenticated={isAuthenticated} />}>
                    <Route
                        path="/schedule"
                        element={
                            <React.Suspense fallback={<ModuleLoading />}>
                                <SchedulePage date={new Date()} />
                            </React.Suspense>
                        }
                    />
                    <Route
                        path="/absence-management"
                        element={
                            <React.Suspense fallback={<ModuleLoading />}>
                                <AbsencePage />
                            </React.Suspense>
                        }
                    />
                    <Route
                        path="/contact-us"
                        element={
                            <React.Suspense fallback={<ModuleLoading />}>
                                <ContactPage />
                            </React.Suspense>
                        }
                    />
                    <Route path="*" element={<ApplicationError />} />
                </Route>
            </Routes>

            <Notification />

            <ToastContainer
                autoClose={AUTO_CLOSE_TOAST_DURATION}
                limit={LIMIT_TOAST_COUNT}
                hideProgressBar={true}
                position="bottom-center"
                theme="colored"
                closeButton={false}
            />
        </>
    );
}
